import { gql } from '@apollo/client';
import { graphql } from '@lib/api';
import { useRouter } from 'next/router';
import Window from '@lib/window-size';
import Select from 'react-select';

// ------------------------------------------------------

import Layout from '@layout/index';
import TabSlider from '@components/tab-slider';
import Container from '@layout/container';

// ------------------------------------------------------

import { grid_list, second_row_filters, label } from './styles.module.scss';

// ------------------------------------------------------

const STATES = [
  { label: 'Ohio', value: 'OH' },
  { label: 'Kentucky', value: 'KY' },
  { label: 'Indiana', value: 'IN' }
];
const DAYS_GONE = ['1', '2', '3', '4', '5', '6', '7'];
const LOCATION_TYPES = [
  { label: 'Cities/Suburbs', value: 'city' },
  { label: 'Counties', value: 'county' },
  { label: 'School Districts', value: 'school_district' },
  { label: 'Zip Codes', value: 'zip' }
];
const SEARCH_TYPES = [
  { label: 'New Listings', value: 'new_listings' },
  { label: 'Price Changes', value: 'price_changes' },
  { label: 'Open Houses', value: 'open_houses' },
  { label: 'New Construction', value: 'new_construction' },
  { headerLabel: 'Properties For Sale', label: 'All Listings', value: 'all_listings' }
];

/**
 * Verify whether the URL has valid params for location, state, and searchtype
 * @param {*} loc
 * @param {*} state
 * @param {*} search
 * @returns {boolean}
 */
export const verifiedParams = (loc, state, search) => {
  const validLoc = LOCATION_TYPES.map((el) => el.value).includes(loc);
  const validState = STATES.map((el) => el.value).includes(state);
  const validSearch = SEARCH_TYPES.map((el) => el.value).includes(search);

  return validLoc && validState && validSearch;
};

export async function getStaticPaths() {
  const paths = [];

  STATES.forEach(({ value: state }) => {
    LOCATION_TYPES.forEach(({ value: loc }) => {
      SEARCH_TYPES.forEach(({ value: search }) => {
        paths.push({ params: { params: [loc, state, search] } });
      });
    });
  });

  return { fallback: 'blocking', paths: paths };
}

export async function getStaticProps(context) {
  const { params } = context.params;
  const [locationType, state, searchType, daysBack] = params;

  if (!verifiedParams(locationType, state, searchType)) {
    return {
      redirect: {
        destination: '/browse',
        permanent: false
      }
    };
  }

  const LOCATIONS_WITH_ON_MARKET_LISTINGS_QUERY = gql`
    query GetLocationsWithOnMarketListings(
      $locationType: BrowseListingsLocationType!
      $searchType: BrowseListingsSearchType!
      $state: String!
      $daysback: Int
    ) {
      locationsWithOnMarketListings(
        locationtype: $locationType
        searchtype: $searchType
        state: $state
        daysback: $daysback
      ) {
        location
        state
        url
        locationType
        searchType
        daysBack
      }
    }
  `;

  const { locationsWithOnMarketListings } = await graphql(LOCATIONS_WITH_ON_MARKET_LISTINGS_QUERY, {
    daysback: daysBack && parseInt(daysBack),
    locationType: locationType.toUpperCase(),
    searchType: searchType.toUpperCase(),
    state: state.toUpperCase()
  });

  return {
    props: {
      locations: locationsWithOnMarketListings || [],
      params: params
    },
    revalidate: 600
  };
}

const SearchByAToZ = (props) => {
  const router = useRouter();
  const { locations, params } = props;
  const [locParam, stateParam, searchParam, daysParam] = params;

  const needsDaysGone = searchParam === 'new_listings' || searchParam === 'price_changes';

  const getFilterUrl = (type, value) => {
    let daysSuffix = needsDaysGone && daysParam ? `/${daysParam || '7'}` : '';
    switch (type) {
      case 'state':
        return `/browse/${locParam}/${value}/${searchParam}${daysSuffix}`;
      case 'search_type':
        if (!['new_listings', 'price_changes'].includes(value)) {
          daysSuffix = '';
        } else {
          daysSuffix = `/${daysParam || '7'}`;
        }
        return `/browse/${locParam}/${stateParam}/${value}${daysSuffix}`;
      case 'location_type':
        return `/browse/${value}/${stateParam}/${searchParam}${daysSuffix}`;
      case 'days_gone':
        return `/browse/${locParam}/${stateParam}/${searchParam}/${value}`;
      default:
        return `/browse/${locParam}/${stateParam}/${searchParam}${daysSuffix}`;
    }
  };

  const handleMobileSelectClick = ({ value }, type) => {
    const filterUrl = getFilterUrl(type, value);
    if (filterUrl !== router.asPath) {
      router.push(filterUrl);
    }
  };

  const foundState = STATES.find((state) => state.value === stateParam);
  const stateLabel = foundState.label;
  const foundLocation = LOCATION_TYPES.find((loc) => loc.value === locParam);
  const locLabel = foundLocation.label;
  const foundSearchType = SEARCH_TYPES.find((type) => type.value === searchParam);
  const searchTypeLabel = foundSearchType.headerLabel || foundSearchType.label;

  const isMobile = Window().width <= 720;

  return (
    <Layout>
      <Container
        backgroundImage={{
          height: '216px',
          src: '/fpo/FPO_Background_header.png',
          width: '1440px'
        }}
        backgroundImageLayout="responsive"
        gridOptions={{ layout: 'single' }}
        priority
        spacing="small"
        theme="has-background-image-with-content-box-shadow"
      >
        <h1>Browse A-Z</h1>

        {isMobile ? (
          <div>
            <span className={label}>Select Search Type</span>
            <Select
              aria-label="Search Type Select"
              defaultValue={foundSearchType}
              options={SEARCH_TYPES}
              onChange={(e) => handleMobileSelectClick(e, 'search_type')}
            />
          </div>
        ) : (
          <TabSlider
            label="Select Search Type"
            items={SEARCH_TYPES.map(({ label, value }) => ({
              isActive: value === searchParam,
              label,
              url: getFilterUrl('search_type', value),
              value
            }))}
          />
        )}

        <div className={second_row_filters}>
          <TabSlider
            label="Select State"
            items={STATES.map(({ label, value }) => {
              return {
                isActive: value === stateParam,
                label,
                url: getFilterUrl('state', value),
                value
              };
            })}
          />

          {needsDaysGone && (
            <TabSlider
              label={'Select Days Back'}
              items={DAYS_GONE.map((day) => {
                return {
                  isActive: day === daysParam,
                  label: day,
                  url: getFilterUrl('days_gone', day),
                  value: day
                };
              })}
            />
          )}
        </div>
        {isMobile ? (
          <div>
            <span className={label}>Select Search Type</span>
            <Select
              aria-label="Location Type Select"
              defaultValue={foundLocation}
              options={LOCATION_TYPES}
              onChange={(e) => handleMobileSelectClick(e, 'location_type')}
            />
          </div>
        ) : (
          <TabSlider
            label="Select Location Type"
            items={LOCATION_TYPES.map(({ label, value }) => ({
              isActive: value === locParam,
              label,
              url: getFilterUrl('location_type', value),
              value
            }))}
          />
        )}

        <p>
          {`${stateLabel} ${locLabel} with ${searchTypeLabel}. Duplicates may exist in
        areas covered by more than one MLS and contain different listings.`}
        </p>
        <ul className={grid_list}>
          {locations.map((loc) => (
            <li key={loc.location}>
              <a href={loc.url}>{loc.location}</a>
            </li>
          ))}
        </ul>
      </Container>
    </Layout>
  );
};

export default SearchByAToZ;
